// Aggregates sent to the back to generate the Journal AI insights (SFB-1288).
//
// They are computed straight from the trades in the store with the same meaning the stats sections
// give them (profit = sum of netProfit), so what the AI says matches what the charts show. The PnL
// section builds its own aggregates because they depend on the trading plan target.

const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const MAX_ROWS = 8;

const round = (value) => Math.round(value * 100) / 100;

// Trades come back from the persisted store as ISO strings, so never assume a Date.
const toDate = (value) => {
    const date = value ? new Date(value) : null;
    return date && !Number.isNaN(date.getTime()) ? date : null;
};

const profit = (trade) => Number(trade.netProfit) || 0;

function topBy(rows, size) {
    return rows
        .sort((a, b) => Math.abs(b.netProfit) - Math.abs(a.netProfit))
        .slice(0, size);
}

function groupBy(trades, keyOf) {
    const groups = new Map();
    trades.forEach((trade) => {
        const key = keyOf(trade);
        if (key == null) {
            return;
        }
        if (!groups.has(key)) {
            groups.set(key, []);
        }
        groups.get(key).push(trade);
    });
    return groups;
}

function summarize(trades) {
    const wins = trades.filter((trade) => profit(trade) > 0);
    const losses = trades.filter((trade) => profit(trade) < 0);
    return {
        trades: trades.length,
        wins: wins.length,
        losses: losses.length,
        netProfit: round(trades.reduce((sum, trade) => sum + profit(trade), 0)),
    };
}

function buildAssets(trades) {
    const byTicker = groupBy(trades, (trade) => (trade.asset ? trade.asset.ticker : null));
    const assets = [...byTicker.entries()].map(([ticker, list]) => ({
        ticker,
        trades: list.length,
        profit: round(list.filter((t) => profit(t) > 0).reduce((sum, t) => sum + profit(t), 0)),
        loss: round(list.filter((t) => profit(t) < 0).reduce((sum, t) => sum + profit(t), 0)),
        netProfit: round(list.reduce((sum, t) => sum + profit(t), 0)),
    }));
    const markets = {};
    trades.forEach((trade) => {
        const name = trade.asset && trade.asset.market ? trade.asset.market.name : null;
        if (name) {
            markets[name] = (markets[name] || 0) + 1;
        }
    });
    return { assets: topBy(assets, MAX_ROWS), distinctAssets: assets.length, tradesPerMarket: markets };
}

function buildTime(trades) {
    const byWeekday = {};
    const byHour = {};
    const holding = { all: [], winners: [], losers: [] };
    trades.forEach((trade) => {
        const open = toDate(trade.openTime);
        if (!open) {
            return;
        }
        const day = WEEKDAYS[open.getDay()];
        const hour = String(open.getHours()).padStart(2, '0');
        [[byWeekday, day], [byHour, hour]].forEach(([target, key]) => {
            target[key] = target[key] || { trades: 0, netProfit: 0 };
            target[key].trades += 1;
            target[key].netProfit = round(target[key].netProfit + profit(trade));
        });
        const close = toDate(trade.closeTime);
        if (close) {
            const minutes = (close.getTime() - open.getTime()) / 60000;
            holding.all.push(minutes);
            if (profit(trade) > 0) {
                holding.winners.push(minutes);
            } else if (profit(trade) < 0) {
                holding.losers.push(minutes);
            }
        }
    });
    const average = (list) => (list.length ? round(list.reduce((sum, v) => sum + v, 0) / list.length) : null);
    return {
        distinctDays: Object.keys(byWeekday).length,
        byWeekday,
        byHourOfDay: byHour,
        averageHoldingMinutes: {
            all: average(holding.all),
            winners: average(holding.winners),
            losers: average(holding.losers),
        },
    };
}

function buildStrategy(trades) {
    const withStrategy = trades.filter((trade) => trade.strategy && trade.strategy.name);
    if (!withStrategy.length) {
        return null;
    }
    const byStrategy = [...groupBy(withStrategy, (trade) => trade.strategy.name).entries()]
        .map(([name, list]) => ({ name, ...summarize(list) }));
    const byStrategyAndAsset = [...groupBy(withStrategy, (trade) =>
        trade.asset ? `${trade.strategy.name}|${trade.asset.ticker}` : null).entries()]
        .map(([key, list]) => {
            const [strategy, ticker] = key.split('|');
            return { strategy, ticker, ...summarize(list) };
        });
    return {
        tradesWithStrategy: withStrategy.length,
        tradesWithoutStrategy: trades.length - withStrategy.length,
        byStrategy: topBy(byStrategy, MAX_ROWS),
        byStrategyAndAsset: topBy(byStrategyAndAsset, MAX_ROWS),
    };
}

function buildSideMood(trades) {
    const bySide = {};
    groupBy(trades, (trade) => trade.side).forEach((list, side) => {
        bySide[side] = summarize(list);
    });
    const byMood = {};
    groupBy(trades, (trade) => (trade.mood && trade.mood !== 'NONE' ? trade.mood : null)).forEach((list, mood) => {
        byMood[mood] = summarize(list);
    });
    const section = { bySide };
    if (Object.keys(byMood).length) {
        section.byMood = byMood;
    }
    return section;
}

/**
 * @param {Array} trades trades of the current filter, from the store
 * @returns {Object} section key -> aggregates, only for the sections that have data to talk about
 */
export function buildJournalAggregates(trades) {
    if (!trades || !trades.length) {
        return {};
    }
    const sections = {
        assets: buildAssets(trades),
        time: buildTime(trades),
        sideMood: buildSideMood(trades),
    };
    const strategy = buildStrategy(trades);
    if (strategy) {
        sections.strategy = strategy;
    }
    return sections;
}
